import {
  Box,
  Portal,
  Select,
  createListCollection,
  type BoxProps,
} from "@chakra-ui/react";
import { useMemo } from "react";

export type AppSelectOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

export function AppSelect({
  value,
  options,
  onValueChange,
  placeholder = "Select an option",
  ariaLabel,
  width = "100%",
  minW,
  disabled = false,
  invalid = false,
  name,
  onBlur,
}: {
  value: string;
  options: AppSelectOption[];
  onValueChange: (value: string) => void;
  placeholder?: string;
  ariaLabel: string;
  width?: BoxProps["width"];
  minW?: BoxProps["minWidth"];
  disabled?: boolean;
  invalid?: boolean;
  name?: string;
  onBlur?: () => void;
}) {
  const collection = useMemo(
    () => createListCollection({ items: options }),
    [options],
  );

  return (
    <Box width={width} minW={minW}>
      <Select.Root
        collection={collection}
        value={[value]}
        onValueChange={(details) => onValueChange(details.value[0] ?? "")}
        disabled={disabled}
        invalid={invalid}
        size="sm"
        positioning={{ sameWidth: true }}
      >
        <Select.HiddenSelect name={name} />
        <Select.Control>
          <Select.Trigger
            aria-label={ariaLabel}
            bg="bg.panel"
            borderColor={invalid ? "status.negative" : "border.default"}
            borderRadius="4px"
            color="fg.default"
            fontSize="xs"
            fontWeight="650"
            onBlur={onBlur}
            _hover={{ borderColor: "border.strong" }}
            _focusVisible={{ borderColor: "brand.500", boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)" }}
          >
            <Select.ValueText placeholder={placeholder} />
          </Select.Trigger>
          <Select.IndicatorGroup>
            <Select.Indicator />
          </Select.IndicatorGroup>
        </Select.Control>
        <Portal>
          <Select.Positioner>
            <Select.Content
              bg="bg.elevated"
              borderColor="border.default"
              borderRadius="4px"
              boxShadow="0 16px 40px rgba(0,0,0,.28)"
              p="1"
              maxH="300px"
            >
              {collection.items.map((item) => (
                <Select.Item
                  key={item.value}
                  item={item}
                  borderRadius="3px"
                  fontSize="xs"
                  _highlighted={{ bg: "bg.subtle" }}
                >
                  <Select.ItemText>{item.label}</Select.ItemText>
                  <Select.ItemIndicator />
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Positioner>
        </Portal>
      </Select.Root>
    </Box>
  );
}
